import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import logo from "@/assets/mckay-logo.png.asset.json";
import roofingServiceImg from "@/assets/mckay/roofing-service.webp.asset.json";
import solarEnergyImg from "@/assets/mckay/solar-energy.webp.asset.json";
import { Button } from "@/components/ui/button";

const primaryServices = [
  { title: "Roofing Services", subtitle: "Repair, replacement & inspections", img: roofingServiceImg.url, to: "/services/roofing" as const },
  { title: "Solar Services", subtitle: "Panels, batteries & commercial", img: solarEnergyImg.url, to: "/services/solar" as const },
];

const aboutItems = [
  { to: "/about", label: "Our Team" },
  { to: "/why-choose-us", label: "Why Choose Us" },
  { to: "/faq", label: "FAQs" },
  { to: "/expert-tips", label: "Expert Tips" },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<null | "about" | "res" | "svc">(null);
  const [mobileSvcOpen, setMobileSvcOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isContactPage = pathname === "/contact";
  const forcePill = pathname === "/areas-served";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wrapperClasses = [
    "relative",
    "mx-4 sm:mx-6 lg:mx-auto",
    "px-4 sm:px-6",
    "transition-all duration-500 ease-out",
    scrolled || forcePill
      ? "top-4 max-w-6xl rounded-full bg-primary/90 shadow-2xl shadow-black/30 backdrop-blur-xl py-2"
      : "top-6 max-w-7xl bg-transparent py-4",
  ].join(" ");

  return (
    <header className="fixed inset-x-0 top-0 z-40">
      <div className={wrapperClasses}>
        <div
          className={`mx-auto flex items-center justify-between gap-6 transition-all duration-500 ${
            scrolled || forcePill ? "h-14 max-w-6xl" : "h-16 max-w-7xl"
          }`}
        >
          <Link to="/" className="flex shrink-0 items-center" aria-label="McKay Roofing & Solar – Home">
            <img
              src={logo.url}
              alt="McKay Roofing & Solar"
              className={`h-auto w-full max-w-[90px] drop-shadow-md transition-all duration-500 sm:max-w-[110px] ${scrolled ? "max-w-[80px] sm:max-w-[90px]" : ""}`}
            />
          </Link>

          <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex" aria-label="Primary">
            <NavLink to="/" scrolled={scrolled}>Home</NavLink>
            <ServicesDropdown
              isOpen={openMenu === "svc"}
              onOpen={() => setOpenMenu("svc")}
              onClose={() => setOpenMenu(null)}
              scrolled={scrolled}
            />
            <NavLink to="/gallery" scrolled={scrolled}>Gallery</NavLink>
            <NavLink to="/areas-served" scrolled={scrolled}>Areas Served</NavLink>
          </nav>

          {!isContactPage && (
            <div className="hidden shrink-0 lg:block">
              <Button
                asChild
                className={`group h-11 rounded-full px-6 font-bold uppercase tracking-wider shadow-lg transition-colors duration-500 ease-out ${
                  scrolled || forcePill
                    ? "bg-white text-primary hover:bg-white/90"
                    : "bg-primary text-primary-foreground hover:bg-primary/90"
                }`}
              >
                <Link to="/contact">
                  <Phone className="mr-2 h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
                  Get Quote
                </Link>
              </Button>
            </div>
          )}

          <button
            className={`rounded-md p-2 lg:hidden ${scrolled ? "text-primary-foreground" : "text-white"}`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
        aria-hidden={!open}
      >
        <div
          onClick={() => setOpen(false)}
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        />
        <div
          className={`absolute inset-y-0 right-0 flex w-[80%] max-w-sm flex-col overflow-hidden bg-primary text-primary-foreground shadow-2xl transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        >
          <div className="flex items-center justify-between px-5 pt-5">
            <Link to="/" onClick={() => setOpen(false)} aria-label="McKay Roofing & Solar – Home">
              <img src={logo.url} alt="McKay Roofing & Solar" className="h-auto w-[110px]" />
            </Link>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/40 text-primary-foreground transition hover:bg-primary-foreground/10"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 px-6 pt-8 pb-8">
            <MobileNavItem to="/" onClick={() => setOpen(false)}>Home</MobileNavItem>

            <button
              type="button"
              onClick={() => setMobileSvcOpen((v) => !v)}
              aria-expanded={mobileSvcOpen}
              className="flex w-full items-center justify-between border-b border-primary-foreground/15 py-4 text-left text-[15px] font-bold uppercase tracking-[0.22em]"
            >
              <span>Services</span>
              <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${mobileSvcOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileSvcOpen && (
              <div className="grid grid-cols-2 gap-3 py-4">
                {primaryServices.map((s) => (
                  <Link
                    key={s.title}
                    to={s.to}
                    onClick={() => setOpen(false)}
                    className="group relative aspect-[3/4] overflow-hidden rounded-xl bg-primary-foreground/10"
                  >
                    <img src={s.img} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-2.5 text-white">
                      <div className="text-[13px] font-bold leading-tight">{s.title}</div>
                      <div className="mt-1 text-[10px] leading-tight text-white/80">{s.subtitle}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <MobileNavItem to="/gallery" onClick={() => setOpen(false)}>Gallery</MobileNavItem>
            <MobileNavItem to="/areas-served" onClick={() => setOpen(false)}>Areas Served</MobileNavItem>

            {!isContactPage && (
              <div className="pt-8">
                <Button asChild className="h-12 w-full rounded-full bg-primary-foreground font-bold uppercase tracking-wider text-primary hover:bg-primary-foreground/90">
                  <Link to="/contact" onClick={() => setOpen(false)}>
                    <Phone className="mr-2 h-4 w-4" />
                    Get Free Quote
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function NavLink({ to, children, scrolled }: { to: string; children: React.ReactNode; scrolled: boolean }) {
  return (
    <Link
      to={to}
      className={`group relative px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 hover:text-white hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.9)] ${
        scrolled ? "text-primary-foreground/90" : "text-white/90"
      }`}
      activeProps={{ className: "[&_span]:scale-x-100" }}
      activeOptions={{ exact: true }}
    >
      {children}
      <span className="pointer-events-none absolute inset-x-4 -bottom-0.5 h-0.5 origin-center scale-x-0 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)] transition-transform duration-300 group-hover:scale-x-100" />
    </Link>
  );
}

function MobileLink({
  to, children, onClick, small,
}: { to: string; children: React.ReactNode; onClick: () => void; small?: boolean }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`block rounded-md px-3 py-2 ${small ? "text-sm text-primary-foreground/80" : "text-base font-semibold"} hover:bg-primary-foreground/10 hover:text-primary-foreground`}
    >
      {children}
    </Link>
  );
}

function MobileNavItem({ to, children, onClick }: { to: string; children: React.ReactNode; onClick: () => void }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block border-b border-primary-foreground/15 py-4 text-[15px] font-bold uppercase tracking-[0.22em] transition hover:text-white"
    >
      {children}
    </Link>
  );
}

function ServicesDropdown({
  isOpen, onOpen, onClose, scrolled,
}: { isOpen: boolean; onOpen: () => void; onClose: () => void; scrolled: boolean }) {
  return (
    <div className="relative" onMouseEnter={onOpen} onMouseLeave={onClose}>
      <button
        type="button"
        onClick={() => (isOpen ? onClose() : onOpen())}
        className={`group relative inline-flex items-center gap-1 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 hover:text-white hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.9)] ${
        scrolled ? "text-primary-foreground/90" : "text-white/90"
      }`}>
        Services
        <ChevronDown className={`h-3 w-3 opacity-70 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
        <span className={`pointer-events-none absolute inset-x-4 -bottom-0.5 h-0.5 origin-center rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.9)] transition-transform duration-300 ${isOpen ? "scale-x-100" : "scale-x-0"}`} />
      </button>
      <div
        className={`absolute left-1/2 top-full z-50 w-[36rem] -translate-x-1/2 pt-3 transition-all duration-200 ${
          isOpen ? "pointer-events-auto opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-1"
        }`}
      >
        <div className="overflow-hidden rounded-2xl border border-primary/40 bg-primary p-3 text-primary-foreground shadow-2xl">
          <div className="grid grid-cols-2 gap-3">
            {primaryServices.map((s) => (
              <Link
                key={s.title}
                to={s.to}
                onClick={onClose}
                className="group relative aspect-[4/5] overflow-hidden rounded-xl bg-primary/60"
              >
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                  <div className="text-base font-bold leading-tight">{s.title}</div>
                  <div className="mt-1 text-xs text-white/80">{s.subtitle}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileServices({ open, onToggle, onClose }: { open: boolean; onToggle: () => void; onClose: () => void }) {
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-semibold hover:bg-primary-foreground/10"
      >
        <span>Services</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-3 border-l border-primary-foreground/20 pl-3">
          {primaryServices.map((s) => (
            <Link
              key={s.title}
              to={s.to}
              onClick={onClose}
              className="block rounded-md px-3 py-2 text-sm text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              {s.title}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}
